import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import RewardFilters from './components/RewardFilters';
import RewardCard from './components/RewardCard';
import RewardFormModal from './components/RewardFormModal';
import KpiCard from '../components/KpiCard';
import { rewards as seedRewards } from '@/mocks/rewards';

import type { Reward, RewardDraft } from '@/types/reward';

export default function RewardsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rewards, setRewards] = useState<Reward[]>(seedRewards);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All categories');
  const [status, setStatus] = useState('All statuses');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Reward | null>(null);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setEditing(null);
      setModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rewards.filter((reward) => {
      const matchesQuery = !q || reward.name.toLowerCase().includes(q) || reward.description.toLowerCase().includes(q);
      const matchesCategory = category === 'All categories' || reward.category === category;
      const matchesStatus =
        status === 'All statuses' ||
        (status === 'Active' && reward.status === 'active') ||
        (status === 'Disabled' && reward.status === 'disabled');
      return matchesQuery && matchesCategory && matchesStatus;
    });
  }, [query, category, status, rewards]);

  const activeCount = rewards.filter((reward) => reward.status === 'active').length;
  const totalStock = rewards.reduce((sum, reward) => sum + reward.stock, 0);
  const totalRedeemed = rewards.reduce((sum, reward) => sum + reward.redeemed, 0);

  const stats = [
    {
      id: 'active',
      label: 'Active rewards',
      value: activeCount.toString(),
      icon: 'ri-gift-2-line',
      tone: 'primary' as const,
      delta: 'Live in app',
      trend: 'up' as const,
      note: 'Available for members',
    },
    {
      id: 'stock',
      label: 'Total inventory stock',
      value: totalStock.toLocaleString(),
      icon: 'ri-stack-line',
      tone: 'accent' as const,
      note: 'Units ready for claim',
    },
    {
      id: 'redeemed',
      label: 'Total redeemed',
      value: totalRedeemed.toLocaleString(),
      icon: 'ri-check-double-line',
      tone: 'secondary' as const,
      note: 'All-time successful claims',
    },
    {
      id: 'catalog',
      label: 'Catalog items',
      value: rewards.length.toString(),
      icon: 'ri-grid-line',
      tone: 'background' as const,
      note: 'Distinct reward offers',
    },
  ];

  const handleSave = (draft: RewardDraft) => {
    if (editing) {
      setRewards((prev) => prev.map((reward) => (reward.id === draft.id ? { ...reward, ...draft, redeemed: reward.redeemed } : reward)));
    } else {
      setRewards((prev) => [
        {
          id: draft.id,
          name: draft.name,
          description: draft.description,
          category: draft.category,
          pointsCost: draft.pointsCost,
          stock: draft.stock,
          status: draft.status,
          redeemed: 0,
          image: draft.image,
        },
        ...prev,
      ]);
    }
    setModalOpen(false);
    setEditing(null);
  };

  const handleToggle = (id: string) => {
    setRewards((prev) =>
      prev.map((reward) =>
        reward.id === id ? { ...reward, status: reward.status === 'active' ? 'disabled' : 'active' } : reward,
      ),
    );
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (reward: Reward) => {
    setEditing(reward);
    setModalOpen(true);
  };

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-extrabold text-2xl text-foreground-950 tracking-tight">Reward catalog</h3>
          <p className="text-sm text-foreground-500 mt-0.5">Manage rewards, inventory, points costs, and active status.</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-bold shadow-md shadow-primary-500/25 hover:from-primary-600 hover:to-primary-700 hover:shadow-lg hover:shadow-primary-500/35 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
        >
          <i className="ri-add-line text-base"></i> Create reward
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <KpiCard
            key={stat.id}
            label={stat.label}
            value={stat.value}
            icon={stat.icon}
            tone={stat.tone}
            delta={stat.delta}
            trend={stat.trend}
            note={stat.note}
          />
        ))}
      </div>

      <RewardFilters
        query={query}
        onQueryChange={setQuery}
        category={category}
        onCategoryChange={setCategory}
        status={status}
        onStatusChange={setStatus}
        resultCount={filtered.length}
        total={rewards.length}
      />

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-background-200/90 bg-background-50/95 p-12 text-center shadow-xs">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-3 shadow-inner">
            <i className="ri-gift-line text-2xl"></i>
          </div>
          <h4 className="font-heading font-bold text-foreground-950 text-base">No rewards found</h4>
          <p className="text-sm text-foreground-500 mt-1">Try clearing your filters or create a new reward offer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((reward) => (
            <RewardCard key={reward.id} reward={reward} onEdit={openEdit} onToggle={handleToggle} />
          ))}
        </div>
      )}

      {modalOpen && (
        <RewardFormModal
          open={modalOpen}
          reward={editing}
          onSave={handleSave}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
